/**
 * @author Muhammad Omran
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2019-12-17
 * @description Implementation of the Claiming Profile Modal
 * @filename claim-profile-modal.tsx
 */
import {
  MediaAsset as GraphqlMediaAsset,
  MediaAssetFile as GraphMediaAssetFile,
  MediaFormatKind,
  PreviewKind,
  TopLevelMediaKind,
  UserRequest as GraphqlClaimRequest,
} from "@radivision/graphql";
import React from "react";
import { MediaAssetFilePageFormInput, MediaAssetPageFormInput } from "../../component-configuration/cms-pages";
import { ERROR_MESSAGES } from "../../constants/errors-constants";
import { ACCEPT_DOCUMENT_FORMATS } from "../../constants/general-constants";
import { ClaimRequest } from "../../utilities/mutation-operation/claim-request";
import { MediaAsset } from "../../utilities/mutation-operation/media-asset";
import { MediaAssetFile } from "../../utilities/mutation-operation/media-asset-file";
import { DocumentHelper } from "../../utilities/doc-helper";
import { getCleanUuid } from "../../utilities/general";
import { MediaAssetFile as FileUtility } from "../../utilities/media-asset-file";
import { RdvButton } from "../page-framework/rdv-button";
import { Loader } from "../page/loader";
import { AccountVerificationModal } from "./register-and-login/account-verification-modal";
import { MessageModal } from "./register-and-login/message-modal";
import { SVG } from "../../component-configuration/svgs";
/**
 * number defining maximum allowed file size
 */
const MAX_FILE_SIZE: number = 10485760;

/**
 *
 */
const ALLOWED_DOCUMENT_TYPES: string[] = [MediaFormatKind.PNG, MediaFormatKind.JPEG, "image/jpeg", "application/pdf"];

/**
 * The props of ClaimProfileModal component.
 *
 * @interface
 */
interface ClaimProfileModalProps {
  /**
   * A function that changes the current modal.
   *
   * @type {function(modal: any, props:Object):void}
   */
  changeModal: (modal: any, props?: {}) => void;

  /**
   * The ID of the CreateAccount class.
   *
   * @type {string}
   */
  id: string;

  /**
   * the id of the person profile
   *
   * @type {string}
   * @memberof ClaimProfileModalProps
   */
  profileId: string;

  /**
   * the name of the person profile
   *
   * @type {string}
   * @memberof ClaimProfileModalProps
   */
  profileName: string;

  /**
   * the email of the person who aims to claim the profile
   *
   * @type {string}
   * @memberof ClaimProfileModalProps
   */
  email?: string;

  /**
   * A function that navigates to the technical support pages and closes the modal.
   *
   * @type {function():void}
   */
  navigateToTechnicalSupport: () => void;

  /**
   * A function that navigates to the terms and conditions pages and closes the modal.
   *
   * @type {function():void}
   */
  navigateToTermsAndConditions: () => void;

  /**
   * A function that returns a promise to resend the account verification email.
   *
   * @type {function(email: string):Promise<any>}
   */
  resendAccountVerificationEMail: (email: string) => Promise<any>;

  /**
   * A function that sets the title of the modal.
   *
   * @type {function(title:string):void}
   * @memberof CreateAccountProps
   */
  setTitle: (title: string) => void;

  /**
   * A function that signs up the user.
   *
   * @type {function(email:string, password:string, name:string):Promise<*>}
   * @memberof CreateAccountProps
   */
  signUpUser: (auth: {
    email?: string;
    password?: string;
    name?: string;
    provider?: "google" | "facebook" | "linkedin" | "twitter";
    authObject?: any;
  }) => Promise<any>;

  /**
   * the type of the profile aiming to claim
   *
   * @type {"person" | "entity"}
   */
  type: "person" | "entity";

  /**
   * the current logged in user id
   *
   * @type {string}
   */
  userId?: string;

  /**
   * the current logged in user name
   *
   * @type {string}
   */
  username?: string;

  /**
   * A function that returns a flag which identifies if a password is valid.
   *
   * @type {function(password:string):boolean}
   */
  validatePassword: (password: string) => boolean;
}

/**
 *
 *
 * @interface StaticFile
 */
interface StaticFile {
  /**
   * blob contains the actual bytes of original file | manipulated one
   */
  fileBinaryContent: Blob;
  /**
   * File object contains needed information of uploaded file
   */
  imageObject?: File | string | any;
  /**
   * number defining file size in bytes
   */
  fileSize: number;
  /**
   * string defining file name
   */
  fileName: string;
  /**
   * string defining file type defined in file header not file extension
   */
  mimeType: string;
}

/**
 * The state of the ClaimProfileModal component.
 *
 * @interface
 */
interface ClaimProfileModalState {
  /**
   * The email address of the user
   *
   * @type {string}
   */
  email: string;

  /**
   * A current error.
   *
   * @type {AWS.AWSError}
   */
  error: any | { code: string; message: string };

  /**
   * A flag which is true if there is an error.
   *
   * @type {boolean}
   */
  hasError: boolean;

  /**
   * A text value that indicates if the password if hidden or shown.
   *
   * @type {string}
   */
  hideOrShow: string;

  /**
   * The Legal Document files
   *
   * @type {StaticFile[]}
   */
  legalDocFiles: StaticFile[];

  /**
   * legal doc file uploading errors if any
   *
   * @type {string}
   */
  legalDocFileError: string;

  /**
   * fire the progress overlay when submit button is clicked
   *
   * @type {boolean}
   */
  loading: boolean;

  /**
   * The password chosen by user
   *
   * @type {string}
   */
  password: string;

  /**
   * A flag which is true if the password is valid
   *
   * @type {boolean}
   */
  passwordIsValid: boolean;

  /**
   * A flag which is true if the password and duplicate password match.
   *
   * @type {boolean}
   */
  passwordMatch: boolean;

  /**
   * The re-entered password chosen by user
   *
   * @type {string}
   */
  reenteredPassword: string;

  /**
   * The type of the password field which is either "text" or "password"
   *
   * @type {("password"|"text")}
   */
  type: "password" | "text";

  /**
   * the current logged in user id
   *
   * @type {string}
   */
  userId: string;

  /**
   * the current logged in user name
   *
   * @type {string}
   */
  username: string;

  /**
   *
   *
   * @type null
   * @memberof ClaimProfileModalState
   */
  hasRegistered?: boolean;
}

/**
 *
 * A React component that renders the create account modal.
 *
 * @extends {React.Component}
 */
export class ClaimProfileModal extends React.Component<ClaimProfileModalProps, ClaimProfileModalState> {
  passwordInput: React.RefObject<HTMLInputElement>;
  passwordConfirmInput: React.RefObject<HTMLInputElement>;
  /**
   * Constructor.
   *
   * @param {ClaimProfileModalProps} props The props of the component.
   */
  constructor(props: ClaimProfileModalProps) {
    super(props);
    this.passwordInput = React.createRef();
    this.passwordConfirmInput = React.createRef();

    this.changeState = this.changeState.bind(this);
    this.contactTechnicalSupport = this.contactTechnicalSupport.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.navigateToTermsAndConditions = this.navigateToTermsAndConditions.bind(this);
    this.onPasswordChange = this.onPasswordChange.bind(this);
    this.onReenterPasswordChange = this.onReenterPasswordChange.bind(this);
    this.onSelectFile = this.onSelectFile.bind(this);
    this.claimProfile = this.claimProfile.bind(this);
    this.submitDocumentAssets = this.submitDocumentAssets.bind(this);

    this.state = {
      email: props.email ? props.email : "",
      userId: props.userId ? props.userId : "",
      username: props.username ? props.username : "",
      error: null,
      hasError: false,
      hideOrShow: "show",
      legalDocFiles: [],
      legalDocFileError: "",
      loading: false,
      password: "",
      passwordIsValid: true,
      passwordMatch: true,
      reenteredPassword: "",
      type: "password",
      hasRegistered: false,
    };
    this.verifyAccount = this.verifyAccount.bind(this);
  }

  /**
   * Changes the type of password input field from password to text and vice versa
   */
  changeState() {
    const oldState: string = this.state.type;
    const isTextOrHide: boolean = oldState === "password";
    const newState: "text" | "password" = isTextOrHide ? "text" : "password";
    const hideOrShow: "Hide" | "Show" = isTextOrHide ? "Hide" : "Show";

    this.setState({
      hideOrShow,
      type: newState,
    });
  }

  /**
   * Invoked by the React framework after the component output has been rendered to the DOM
   */
  componentDidMount() {
    this.setState({
      email: this.props.email ? this.props.email : "",
      error: null,
      hasError: false,
    });
  }

  /**
   * Invokes the technical support and closes the modal.
   *
   * @param {React.MouseEvent<HTMLAnchorElement>} e The click event
   */
  contactTechnicalSupport(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    this.props.navigateToTechnicalSupport();
  }

  /**
   * Changes the state's email property as user input changes.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event The change event on the email input field.
   */
  handleEmailChange(event: React.ChangeEvent<HTMLInputElement>) {
    event.preventDefault();
    this.setState({
      email: event.target.value,
    });
  }

  /**
   * Navigates to the terms and conditions page and closes the modal.
   *
   * @param {React.MouseEvent<HTMLAnchorElement>} e The click event
   */
  navigateToTermsAndConditions(e: React.MouseEvent<HTMLAnchorElement>): void {
    e.preventDefault();
    this.props.navigateToTermsAndConditions();
  }

  /**
   * Changes the state's email property as user input changes.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event The change event on the password input field.
   */
  onPasswordChange(event: React.KeyboardEvent<HTMLInputElement>) {
    event.preventDefault();
    this.setState({
      hasError: false,
      password: this.passwordInput.current.value,
      passwordIsValid: this.props.validatePassword(this.passwordInput.current.value),
      passwordMatch:
        this.passwordInput.current.value.length === 0 ||
        this.passwordInput.current.value === this.state.reenteredPassword,
    });
  }

  /**
   * Invoked when the re-entered password changes.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} event The event supplied when the re-entered password changes.
   */
  onReenterPasswordChange(event: React.KeyboardEvent<HTMLInputElement>): void {
    event.preventDefault();
    this.setState({
      hasError: false,
      reenteredPassword: this.passwordConfirmInput.current.value,
      passwordMatch:
        this.state.password.length === 0 || this.passwordConfirmInput.current.value === this.state.password,
    });
  }

  /**
   * Signs up user with email and password.
   *
   * @param {React.FormEvent} event The form submit event.
   */
  onSubmit(event: React.FormEvent): Promise<any> {
    let promise: Promise<any> = Promise.resolve();

    event.preventDefault();
    // reset model state
    this.setState({
      hasError: false,
      loading: true,
    });

    promise = promise.then(() => {
      let internalPromise: Promise<any>;
      switch (this.props.type) {
        case "entity":
          internalPromise = Promise.resolve();
          break;

        case "person":
          internalPromise = this.handlePersonIdentity();
          break;

        default:
          internalPromise = Promise.reject("UNKNOWN MODEL TYPE");
      }

      return internalPromise;
    });

    promise = promise.then(() => {
      // VALIDATION
      if (!this.state.email || this.state.email.length < 1) {
        throw new Error("INVALID USER IDENTITY EMAIL");
      }

      if (!this.state.legalDocFiles || this.state.legalDocFiles.length < 1) {
        throw new Error("MISSING_DOCUMENT_FILES");
      }
    });

    promise = promise.then(() => {
      return this.claimProfile();
    });

    promise = promise.then((request: GraphqlClaimRequest) => {
      // HANDLE CLAIM REQUEST SUCCESS
      this.props.changeModal(MessageModal, {
        messageTitle: "Thank you!",
        message: (
          <span>
            We're excited to welcome you to the Radivision Community.
            <br />
            We will review your submission and be back in touch soon!
          </span>
        ),
      });

      if (this.props.type === "person") {
        return import("../../authentication/cognito-client").then((module) => {
          return module.CognitoClient.signOut(true);
        });
      }
      return Promise.resolve();
    });

    promise = promise.catch((err) => {
      console.error(err);
      this.setState({
        hasError: true,
        error: err.code ? err : { code: err.message, message: "" },
      });
    });

    promise = promise.finally(() => {
      this.setState({
        loading: false,
      });
    });

    return promise;
  }

  /**
   *
   *
   * @returns {Promise<any>}
   * @memberof ClaimProfileModal
   */
  handlePersonIdentity(): Promise<any> {
    let promise: Promise<any> = Promise.resolve();

    if (!this.state.hasRegistered) {
      promise = promise.then(() => {
        if (!this.state.passwordIsValid || !this.state.passwordMatch) {
          throw new Error("InvalidPasswordException");
        }

        if (!this.state.email) {
          throw new Error("EmailEmptyException");
        }

        return Promise.resolve();
      });

      promise = promise.then(() => {
        return import("../../authentication/cognito-client").then((module) => {
          return module.CognitoClient.createAccountUsingEmailAndPassword(
            this.state.email,
            this.state.password,
            `${this.props.profileName}`,
          );
        });
      });

      promise = promise.then((value) => {
        if (value === null || value === undefined) {
          throw new Error("invalid sign up response data");
        }

        return ClaimRequest.validateUserClaimRequest(this.state.email);
      });

      promise = promise.then(() => {
        return import("../../authentication/cognito-client").then((module) => {
          return module.CognitoClient.loginWithEmailAndPassword(this.state.email, this.state.password).then((res) => {
            this.setState({
              password: "",
              reenteredPassword: "",
              hasRegistered: true,
            });
            return Promise.resolve();
          });
        });
      });
    }

    return promise;
  }

  /**
   * Promise to send the claim profile data.
   *
   * @returns {Promise<*>} The promise to register the user using the email and password.
   */
  claimProfile(): Promise<any> {
    let promise: Promise<any>;

    // promise chain
    // 1) upload file to s3 bucket
    // 2) createMediaAssetFromFile
    // 3) send the form with claim profile data
    // 4) stop loading
    promise = this.submitDocumentAssets();

    // create media asset from upload and processed media asset file(s)
    promise = promise.then((ids: string[]) => {
      if (!ids) {
        throw new Error("INVALID MEDIA ASSET FILEs IDs");
      }
      const input: MediaAssetPageFormInput = {
        files: ids,
        description: "Media asset holding claiming information",
        topMediaLevel: TopLevelMediaKind.DOCUMENT,
      };
      return MediaAsset.addMediaAsset(input);
    });

    // validate the created media asset then create a user request input with
    // approbate data to request a claim form .
    promise = promise.then((mediaAsset: GraphqlMediaAsset) => {
      if (!mediaAsset) {
        throw new Error("INVALID MEDIA ASSET, DOCUMENTs CORRUPTED");
      }
      const claimInput: any = {
        item: this.props.profileId,
        // description:"",
        personEmail: this.state.email,
        previews: [{ kind: PreviewKind.SQUARE, mediaAsset: mediaAsset.id }],
      };

      return ClaimRequest.createClaimRequest(claimInput);
    });

    // handle any error in promise chain
    // promise = promise.catch(err => {
    //   console.error(err);
    //   this.setState({
    //     hasError: true,
    //     error: { code: "", message: "" }
    //   });
    // });

    return promise;
  }

  /**
   *
   *
   * @returns {Promise<any>}
   * @memberof ClaimProfileModal
   */
  submitDocumentAssets(): Promise<any> {
    let promise: Promise<any>;

    const documents: {
      fileBinaryContent: Blob;
      fileSize: number;
      fileName: string;
      mimeType: string;
    }[] = this.state.legalDocFiles;

    const uploadPromises: Promise<any>[] = documents.map((document) => {
      if (document.fileSize > MAX_FILE_SIZE) {
        throw new Error("MAX_FILE_LIMIT");
      }

      return DocumentHelper.uploadDocumentToS3(document).then((res: any) => {
        const input: MediaAssetFilePageFormInput = {
          description: `Legal papers for claim profile`,
          name: `${res.Key}`,
          sizeInBytes: document.fileSize,
          format: document.mimeType,
        };
        return MediaAssetFile.addMediaAssetFile(input);
      });
    });

    promise = Promise.all(uploadPromises).then((values: GraphMediaAssetFile[]) => {
      const IDS: string[] = values.map((mediaAssetFile: GraphMediaAssetFile) => {
        return mediaAssetFile.id;
      });

      return Promise.resolve(IDS);
    });

    return promise;
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div>
        <div id="createAccount" className="claim-profile createAccount add-media-modal">
          <h3 className="text-center mb-4 font-weight-bold">
            {this.props.type === "person"
              ? "Claim Your Radivision Personal Profile"
              : "Claim Your Radivision Company Page"}
          </h3>
          {this.props.type === "person" ? (
            <p className="claimDiscription">Please sign up with the email that will claim the profile</p>
          ) : null}
          <form id="claim-profile-form" onSubmit={this.onSubmit}>
            <div className="row">
              <div className="col-md-12 infoLogin row">
                <div className="col-md-6 form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="createAccountName"
                    placeholder="Name"
                    value={this.props.type === "person" ? this.props.profileName : this.props.username}
                    disabled={true}
                  />
                </div>
                {this.props.type === "entity" ? (
                  <div className="col-md-6 form-group">
                    <label>Company</label>
                    <input
                      type="text"
                      className="form-control"
                      id="the-company"
                      placeholder="company"
                      value={this.props.profileName}
                      disabled={true}
                    />
                  </div>
                ) : null}
                {this.props.type === "person" ? (
                  <div className="col-md-6 form-group">
                    <label>
                      Email
                      <span className="required-star">*</span>
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      id="createAccountEmail"
                      placeholder="Your email"
                      onChange={this.handleEmailChange}
                      value={this.props.email ? this.props.email : this.state.email}
                      disabled={this.props.email || this.state.loading ? true : false}
                      required={true}
                    />
                  </div>
                ) : null}
                {this.props.type === "person" ? (
                  <div className="col-md-6 form-group">
                    <label>
                      Password
                      <span className="required-star">*</span>
                    </label>
                    <input
                      type={this.state.type}
                      ref={this.passwordInput}
                      className={`form-control ${this.state.passwordIsValid ? "" : "is-invalid"}`}
                      id="createAccountPassword"
                      placeholder="8+ characters: upper, lower, number, & special"
                      onKeyUp={this.onPasswordChange}
                      required={true}
                      disabled={this.state.loading}
                    />
                    {this.state.passwordIsValid ? null : (
                      <div className="invalid-feedback">{ERROR_MESSAGES.INVALID_PASSWORD_MESSAGE}</div>
                    )}
                  </div>
                ) : null}
                {this.props.type === "person" ? (
                  <div className="col-md-6 form-group">
                    <label>
                      Confirm Password
                      <span className="required-star">*</span>
                    </label>
                    <input
                      type={this.state.type}
                      className={`form-control ${this.state.passwordMatch ? "" : "is-invalid"}`}
                      id="createAccountConfirmPassword"
                      placeholder="Confirm Password"
                      onKeyUp={this.onReenterPasswordChange}
                      ref={this.passwordConfirmInput}
                      required={true}
                      disabled={this.state.loading}
                    />
                    {this.state.passwordMatch ? null : (
                      <div className="invalid-feedback">Does not match the password.</div>
                    )}
                  </div>
                ) : null}
                <div className="upload-evedince col-md-12">
                  <div className="form-sep">
                    <span className="line" />
                    <span className="text">
                      {this.props.type === "person" ? "Please Verify Your Identity" : "Please Verify Your Ownership"}
                    </span>
                    <span className="line" />
                  </div>
                  <div className="instructions">
                    {this.props.type === "person" ? (
                      <div>
                        <p>Please submit a government-issued ID</p>
                        <ul>
                          <li>Driver's License, State or National ID or Passport are acceptable.</li>
                          <li>Images must be in color and all corners showing.</li>
                          <li>Files format must be JPEG, PNG or PDF.</li>
                          <li>File size must not exceed 10MB.</li>
                        </ul>
                      </div>
                    ) : (
                      <div>
                        <p>Please submit a government-issued ID</p>
                        <ul>
                          <li>
                            Personal Verification: Driver's License, State or National ID or Passport are acceptable.
                          </li>
                          <li>Company Verification: Government-issued company or corporate registration.</li>
                          <li>Images must be in color and all corners showing.</li>
                          <li>Files format must be JPEG, PNG or PDF.</li>
                          <li>File size must not exceed 10MB.</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    className="legal-doc"
                    accept={ACCEPT_DOCUMENT_FORMATS}
                    id="legal-doc"
                    multiple={true}
                    onChange={(event) => this.onSelectFile(event)}
                    disabled={this.state.loading}
                  />
                  <label className="btn btn-radivision outline" htmlFor="legal-doc">
                    {SVG.image} Upload Files
                  </label>
                  <div className="documents-list">
                    {this.state.legalDocFiles.map((file, index) => (
                      <p key={index}>
                        {file.fileName}
                        <span
                          className="rm-doc"
                          onClick={(e) => {
                            this.setState((prevState) => ({
                              legalDocFiles: prevState.legalDocFiles.filter((file, i) => {
                                return i !== index;
                              }),
                            }));
                          }}
                        >
                          {SVG.crosshairsCircle}
                        </span>
                      </p>
                    ))}
                  </div>
                </div>
                {this.props.type === "person" ? (
                  <div className="form-group checkSection col-md-12">
                    <input type="checkbox" value="" required={true} disabled={this.state.loading} />
                    <h5>
                      <a href="#" onClick={this.navigateToTermsAndConditions}>
                        I accept the terms and conditions.
                      </a>
                    </h5>
                  </div>
                ) : null}
                <div className="form-group col-md-12">{this.state.hasError ? this.renderErrorMessage() : null}</div>
              </div>
            </div>
            <RdvButton type="submit" form="claim-profile-form" text="Submit" isLoading={this.state.loading} />
          </form>
        </div>
      </div>
    );
  }

  /**
   * Invoked after a given file is selected to handle displaying the file.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e The source event.
   */
  onSelectFile(e: React.ChangeEvent<HTMLInputElement>): void {
    e.preventDefault();
    const SELECTED_FILES = e.target.files;
    // reset files state
    this.setState({
      legalDocFiles: [],
      hasError: false,
      error: { code: null, message: "" },
    });

    try {
      for (let key = 0; key < SELECTED_FILES.length; key++) {
        const FILE_READER = new FileReader();
        const staticFileInformation: StaticFile = {
          fileBinaryContent: undefined,
          fileSize: SELECTED_FILES[key].size,
          fileName: SELECTED_FILES[key].name,
          mimeType: SELECTED_FILES[key].type,
        };
        // start reading the contents of a specified File. When the read operation is finished,
        // onloadend is triggered
        FILE_READER.readAsArrayBuffer(SELECTED_FILES[key]);
        //  event fired when a request has completed, whether successfully
        // (after load) or unsuccessfully
        FILE_READER.onloadend = (event: ProgressEvent) => {
          // assign target events
          const targetEvent: any = event.target;
          // Get access to the result bytes
          const fileRawData: ArrayBuffer = targetEvent["result"];
          // extract file mime type and extension.
          const FILE_INFO = FileUtility.extractFileData(fileRawData);

          if (!FILE_INFO) {
            this.setState({
              hasError: true,
              error: { code: `INVALID_FILE_MIME_TYPE`, message: "" },
            });
            throw new Error("INVALID_FILE_MIME_TYPE");
          }

          if (!ALLOWED_DOCUMENT_TYPES.includes(FILE_INFO.mime.toLowerCase())) {
            this.setState({
              hasError: true,
              error: { code: `NOT_ALLOWED_FILE_MIME_TYPE`, message: "" },
            });
            throw new Error("NOT_ALLOWED_FILE_MIME_TYPE");
          }

          // assign loaded file mime-type
          staticFileInformation.mimeType = FILE_INFO.mime;
          // TO DO CHECK IF MIME TYPE IS ALLOWED
          // assign loaded file (binary file content)
          staticFileInformation.fileBinaryContent = new Blob([fileRawData]);
          // generate a unique name for file
          // assign new name for uploaded file
          staticFileInformation.fileName = this.generateFileName(FILE_INFO.ext);

          this.setState({
            legalDocFiles: [...this.state.legalDocFiles, staticFileInformation],
          });
        };
      }
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Returns an element containing an error message determined by the current error.
   *
   * @returns {JSX.Element} The element containing the details of the error.
   */
  renderErrorMessage(): JSX.Element {
    let element: JSX.Element;

    if (this.state.hasError && this.state.error) {
      switch (this.state.error.code) {
        case "UsernameExistsException":
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                The email address has already been registered. please{" "}
                <a href="#" onClick={this.contactTechnicalSupport}>
                  contact Radivision's technical support
                </a>
                .
              </h6>
            </div>
          );
          break;
        case "InvalidPasswordException":
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                The password you entered is invalid or does not match the confirm password field. Please check that the
                password you entered meets requirements. For other options please{" "}
                <a href="#" onClick={this.contactTechnicalSupport}>
                  contact Radivision's technical support
                </a>
                .
              </h6>
            </div>
          );
          break;
        case "EmailEmptyException":
          element = (
            <div className="form-group alert alert-danger">
              <h6>You must Enter a valid Email to continue.</h6>
            </div>
          );
          break;
        case "MISSING_DOCUMENT_FILES":
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                You are supposed to upload the scanned copy of an original document which has the name and address of
                your business. Once you are done with this process click on the button which says submit.
              </h6>
            </div>
          );
          break;

        case "MAX_FILE_LIMIT":
          element = (
            <div className="form-group alert alert-danger">
              <h6>This file is too large to upload. The maximum supported file size is: 10 MB for documents.</h6>
            </div>
          );
          break;
        case "InvalidParameterException":
          if (this.state.passwordIsValid || this.state.passwordMatch) {
            element = (
              <div className="form-group alert alert-danger">
                <h6>
                  An unexpected error has occurred. Please{" "}
                  <a href="#" onClick={this.contactTechnicalSupport}>
                    contact Radivision's technical support
                  </a>
                  .
                </h6>
              </div>
            );
          }
          break;

        case "INVALID_FILE_MIME_TYPE":
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                corrupted documents detected, Please scan a copy of an original document.then re-upload your files again
              </h6>
            </div>
          );
          break;

        case "NOT_ALLOWED_FILE_MIME_TYPE":
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                Illegal or invalid mime type detected when trying to upload your files, Please read our Instruction and
                upload a file type from supported types
              </h6>
            </div>
          );
          break;

        default:
          element = (
            <div className="form-group alert alert-danger">
              <h6>
                An unexpected error has occurred. Please{" "}
                <a href="#" onClick={this.contactTechnicalSupport}>
                  contact Radivision's technical support
                </a>
                .
              </h6>
            </div>
          );
      }
    }
    return element;
  }

  /**
   * Returns a promise to verify the account.
   *
   * @param {React.MouseEvent<HTMLAnchorElement>} e The click event
   */
  verifyAccount(e: React.MouseEvent<HTMLAnchorElement>): Promise<any> {
    e.preventDefault();
    return this.props
      .resendAccountVerificationEMail(this.state.email)
      .then(
        (): Promise<void> => {
          this.props.changeModal(AccountVerificationModal, {
            email: this.state.email,
          });
          return Promise.resolve();
        },
      )
      .catch((e: any): void => {
        this.setState({
          error: e,
          hasError: true,
        });
      });
  }

  /**
   *
   *
   * @param {string} fileMimeType
   * @returns {string}
   * @memberof ClaimProfileModal
   */
  generateFileName(ext: string): string {
    // generate a unique name for file
    return `${getCleanUuid()}.${ext}`;
  }
}
